import React from 'react';
import { motion } from 'framer-motion';

const FAQ: React.FC = () => {
  const faqs = [
    {
      category: "Services",
      questions: [
        {
          question: "What types of websites do you develop?",
          answer: "We develop various types of websites including business websites, e-commerce platforms, portfolio sites, dashboards, management systems, and custom web applications tailored to specific business needs."
        },
        {
          question: "Do you provide e-commerce solutions?",
          answer: "Yes, we specialize in e-commerce development using platforms like WooCommerce, custom solutions with PHP/Node.js, and integration with payment gateways like Stripe, PayPal, and Razorpay."
        },
        {
          question: "Can you redesign existing websites?",
          answer: "Absolutely! We offer complete website redesign services to modernize outdated sites, improve user experience, enhance performance, and implement the latest design trends."
        },
        {
          question: "Do you work with content management systems?",
          answer: "Yes, we work with popular CMS platforms like WordPress, and also develop custom CMS solutions for clients who need more control and specific functionality."
        }
      ]
    },
    {
      category: "Technology & Development",
      questions: [
        {
          question: "What technologies do you use?",
          answer: "We use modern technologies including HTML5, CSS3, JavaScript, React, Node.js, Express.js, PHP, MongoDB, MySQL, and various frameworks like Bootstrap and Tailwind CSS."
        },
        {
          question: "Are your websites mobile-responsive?",
          answer: "Yes, all our websites are built with mobile-first responsive design, ensuring optimal performance and user experience across all devices and screen sizes."
        },
        {
          question: "Do you provide API integration?",
          answer: "Yes, we specialize in API integration including payment gateways, social media APIs, third-party services, and custom API development for seamless data flow."
        },
        {
          question: "How do you ensure website security?",
          answer: "We implement multiple security measures including SSL certificates, secure coding practices, regular security updates, data encryption, and protection against common vulnerabilities."
        }
      ]
    },
    {
      category: "Process & Timeline",
      questions: [
        {
          question: "How long does it take to develop a website?",
          answer: "Timeline varies based on complexity: simple business websites (2-4 weeks), complex web applications (6-12 weeks), e-commerce sites (4-8 weeks). We'll provide a detailed timeline after understanding your requirements."
        },
        {
          question: "What is your development process?",
          answer: "Our process includes: 1) Requirement Analysis, 2) Planning & Strategy, 3) UI/UX Design, 4) Development, 5) Testing & Optimization, 6) Deployment & Training, 7) Ongoing Support."
        },
        {
          question: "Do you provide project updates?",
          answer: "Yes, we provide regular updates through email, project management tools, and weekly progress reports. You'll have access to view the development progress throughout the project."
        },
        {
          question: "What if I need changes after the website is live?",
          answer: "We offer post-launch support and maintenance packages. Small changes are usually included in our support plans, while major modifications may have additional costs."
        }
      ]
    },
    {
      category: "Pricing & Payment",
      questions: [
        {
          question: "How do you determine project pricing?",
          answer: "Pricing is based on project complexity, features required, timeline, and technology stack. We provide detailed quotes after understanding your specific requirements and goals."
        },
        {
          question: "Do you offer payment plans?",
          answer: "Yes, we offer flexible payment plans. Typically, we require a deposit to start the project, with milestone-based payments throughout development, and final payment upon completion."
        },
        {
          question: "What are your payment terms?",
          answer: "We accept various payment methods including bank transfer, UPI, and online payment gateways. Payment terms are clearly outlined in our project agreement."
        },
        {
          question: "Do you provide refunds?",
          answer: "We strive for complete client satisfaction. If we're unable to deliver as promised, we offer refunds based on work completed. However, our track record shows 99% client satisfaction."
        }
      ]
    },
    {
      category: "Support & Maintenance",
      questions: [
        {
          question: "Do you provide ongoing support?",
          answer: "Yes, we offer comprehensive maintenance and support packages including bug fixes, security updates, performance optimization, content updates, and technical support."
        },
        {
          question: "What does website maintenance include?",
          answer: "Maintenance includes security updates, performance monitoring, bug fixes, content updates, backup management, and ensuring compatibility with new browsers and devices."
        },
        {
          question: "How quickly do you respond to support requests?",
          answer: "We typically respond within 24 hours for standard support requests and within 4-6 hours for critical issues. Emergency support is available for urgent matters."
        },
        {
          question: "Do you provide training for website management?",
          answer: "Yes, we provide comprehensive training sessions covering content management, basic troubleshooting, and best practices for maintaining your website effectively."
        }
      ]
    },
    {
      category: "SEO & Performance",
      questions: [
        {
          question: "Do you include SEO in your services?",
          answer: "Yes, we implement SEO best practices including proper meta tags, semantic HTML, fast loading times, mobile optimization, and clean URL structures to improve search rankings."
        },
        {
          question: "How do you ensure fast website performance?",
          answer: "We optimize performance through image compression, code minification, caching strategies, CDN integration, and efficient database queries to ensure fast loading times."
        },
        {
          question: "Do you provide analytics setup?",
          answer: "Yes, we can set up Google Analytics, Search Console, and other tracking tools to help you monitor website performance, user behavior, and conversion metrics."
        },
        {
          question: "Can you help with domain and hosting?",
          answer: "We can assist with domain registration recommendations and hosting setup. We work with reliable hosting providers and can help migrate existing websites to better hosting solutions."
        }
      ]
    }
  ];

  return (
    <div className="faq-page">
      {/* Hero Section */}
      <section className="faq-hero">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Frequently Asked Questions</h1>
          <p className="hero-subtitle">
            Find answers to common questions about our services, process, and technology
          </p>
        </motion.div>
        <div className="hero-bg">
          <div className="floating-elements">
            <div className="floating-orb orb-1"></div>
            <div className="floating-orb orb-2"></div>
            <div className="floating-orb orb-3"></div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="faq-content">
        <div className="container">
          {faqs.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              className="faq-category"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
            >
              <h2>{category.category}</h2>
              <div className="faq-list">
                {category.questions.map((faq, faqIndex) => (
                  <motion.div
                    key={faqIndex}
                    className="faq-item"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: faqIndex * 0.05 }}
                  >
                    <details>
                      <summary>{faq.question}</summary>
                      <p>{faq.answer}</p>
                    </details>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="faq-cta">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Still Have Questions?</h2>
          <p>
            Can't find the answer you're looking for? We're here to help!
          </p>
          <div className="cta-buttons">
            <a href="/contact" className="btn-primary">Contact Us</a>
            <a href="https://wa.me/917896353325" className="btn-secondary" target="_blank" rel="noopener noreferrer">
              WhatsApp Us
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default FAQ;
